/**
 * Tagesschau Trend Analyzer
 * Handles keyword tagging, API calls, and D3.js visualization.
 */
import './style.css';

const API_BASE_URL = process.env.API_BASE_URL || 'http://127.0.0.1:5000';

const TRANSLATIONS = {
    de: {
        'header-title': 'Tagesschau Analyse',
        'header-subtitle': 'Analysiere interaktiv die 20 Uhr Nachrichten der',
        'input-placeholder': 'Stichwort eingeben und Enter drücken...',
        'chart-title': 'Trends über die Zeit',
        'loader-text': 'Daten werden gesammelt...',
        'footer-made-by': 'Mit Liebe gemacht von',
        'footer-disclaimer': 'Diese Website ist nicht offiziell von tagesschau.de',
        'subscribe-btn': 'Abonnieren',
        'dsgvo-link': 'DSGVO-Einverständniserklärung',
        'modal-title': 'DSGVO-Einverständniserklärung',
        'modal-p1': 'Ich stimme zu, dass meine Email für den Newsletter erhoben und verarbeitet wird. Die Daten werden vertraulich behandelt und nicht weitergegeben.',
        'modal-p2': 'Hinweis: Sie können Ihre Einwilligung jederzeit für die Zukunft per E-Mail an',
        'modal-p3': 'widerrufen.',
        'footer-sources': 'Quellen',
        'footer-contact': 'Kontakt',
        'alert-fail': 'Suche fehlgeschlagen. Bitte stellen Sie sicher, dass der Backend-Server läuft.',
        'subscribing': 'Abonniere...',
        'subscribed': 'Abonniert!',
        'sub-failed': 'Abonnement fehlgeschlagen.',
        'loading-subtitle': 'Untertitel werden geladen...',
        'no-subtitle': 'Keine Untertitel für dieses Datum verfügbar.',
        'fail-subtitle': 'Laden der Untertitel fehlgeschlagen.',
        'tab-search': '🔍 Stichwortsuche',
        'tab-speakers': '📅 Sprecher-Kalender',
        'tab-transitions': '🔀 Übergänge',
        'speakers-desc': 'Jeder Tag ist nach dem Moderator eingefärbt, der die 20-Uhr-Tagesschau präsentiert hat.',
        'transitions-desc': 'Welcher Sprecher folgt auf welchen? Kantendicke = Häufigkeit des Übergangs.',
        'weekday-title': 'Wochentag-Verteilung',
        'weekday-desc': 'Anteil der Tage pro Wochentag an der Gesamtzahl der moderierten Sendungen.',
        'tab-geo': '🌍 Geo-Analyse',
        'geo-desc': 'Häufigkeit der erwähnten Länder in der Tagesschau.',
        'geo-toggle-abs': 'Absolut',
        'geo-toggle-per': 'Pro 1 Mio. EW',
        'geo-info-title': 'Über die Farbskala',
        'geo-info-text': 'Die Farbskala verwendet eine logarithmische Darstellung, um sowohl kleine Unterschiede als auch große Ausreißer (wie Deutschland oder Krisengebiete) gleichzeitig sichtbar zu machen.',
        'geo-range-fail': 'Keine Datumsbereiche in den Daten gefunden.',
        'tab-coverage': '📊 Datenabdeckung',
        'coverage-desc': 'Anzahl der Tage mit Untertiteln im Vergleich zur Gesamtzahl der Tage pro Monat.'
    },
    en: {
        'header-title': 'Tagesschau Analysis',
        'header-subtitle': 'Interactively analyze the 8pm news from',
        'input-placeholder': 'Type a keyword and press Enter...',
        'chart-title': 'Trends over time',
        'loader-text': 'Gathering data...',
        'footer-made-by': 'Made with love by',
        'footer-disclaimer': 'This website is not official by tagesschau.de',
        'subscribe-btn': 'Subscribe',
        'dsgvo-link': 'DSGVO-Einverständniserklärung',
        'modal-title': 'GDPR Consent',
        'modal-p1': 'I agree that my email will be collected and processed for the newsletter. Data will be treated confidentially and not shared.',
        'modal-p2': 'Note: You can revoke your consent at any time for the future via email to',
        'modal-p3': 'to revoke.',
        'footer-sources': 'Sources',
        'footer-contact': 'Contact',
        'alert-fail': 'Search failed. Please ensure the backend server is running.',
        'subscribing': 'Subscribing...',
        'subscribed': 'Subscribed!',
        'sub-failed': 'Subscription failed.',
        'loading-subtitle': 'Loading subtitle...',
        'no-subtitle': 'No subtitle available for this date.',
        'fail-subtitle': 'Failed to load subtitle.',
        'tab-search': '🔍 Keyword Search',
        'tab-speakers': '📅 Speaker Calendar',
        'tab-transitions': '🔀 Transitions',
        'speakers-desc': 'Each day is coloured by the anchor who presented the 8pm tagesschau.',
        'transitions-desc': 'Which anchor follows which? Edge thickness = frequency of transition.',
        'weekday-title': 'Weekday Distribution',
        'weekday-desc': "Share of episodes per weekday out of each anchor's total broadcast days.",
        'tab-geo': '🌍 Geo Analysis',
        'geo-desc': 'Frequency of countries mentioned in the news.',
        'geo-toggle-abs': 'Absolute',
        'geo-toggle-per': 'Per 1M Cap.',
        'geo-info-title': 'About the Color Scale',
        'geo-info-text': 'The color scale uses a logarithmic representation to make both small differences and large outliers (like Germany or conflict zones) visible simultaneously.',
        'geo-range-fail': 'No date range found in data.',
        'tab-coverage': '📊 Data Coverage',
        'coverage-desc': 'Share of available subtitles per month compared to the total number of days.',
        'geo-no-data': 'No geographical mentions found in this time period.'
    }
};

const state = {
    keywords: new Set(),
    data: {},
    isLoading: false,
    chartVisible: false,
    language: 'de',
    speakersLoaded: false,
    transitionsLoaded: false,
    geoLoaded: false,
    geoDateRange: { start: null, end: null },
    geoDensity: [],
    coverageLoaded: false,
    coverageData: []
};

// DOM Elements
// DOM Elements
const keywordInput = document.getElementById('keyword-input');
const tagContainer = document.getElementById('tag-container');
const chartSection = document.getElementById('chart-section');
const closeChartBtn = document.getElementById('close-chart');
const appContainer = document.getElementById('app-container');
const loader = document.getElementById('loader');

// Colors for the chart - Serious, Editorial Palette
const COLORS = [
    '#1d4ed8', '#059669', '#dc2626', '#d97706', '#7c3aed',
    '#0891b2', '#db2777', '#ea580c', '#0d9488', '#4f46e5'
];

// ─── Fullscreen helpers ───────────────────────────────────────────────────────

let _fullscreenEl = null;

function setupExpandable(el, { infoTooltip } = {}) {
    el.classList.add('chart-expandable');

    // Info icon (optional)
    if (infoTooltip) {
        const info = document.createElement('button');
        info.className = 'chart-info-btn';
        info.setAttribute('aria-label', 'Info');
        info.setAttribute('data-tooltip', infoTooltip);
        info.textContent = 'ⓘ';
        el.appendChild(info);
    }

    // Expand button
    const btn = document.createElement('button');
    btn.className = 'chart-expand-btn';
    btn.title = 'Vollbild';
    btn.setAttribute('aria-label', 'Vollbild');
    btn.innerHTML = '⛶';
    el.appendChild(btn);

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        enterFullscreen(el);
    });
}

function enterFullscreen(el) {
    if (_fullscreenEl) exitFullscreen();
    _fullscreenEl = el;

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.id = 'fullscreen-backdrop';
    backdrop.addEventListener('click', exitFullscreen);
    document.body.appendChild(backdrop);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.id = 'fullscreen-close';
    closeBtn.setAttribute('aria-label', 'Schließen');
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', exitFullscreen);
    document.body.appendChild(closeBtn);

    el.classList.add('chart-fullscreen');
    document.body.style.overflow = 'hidden';
}

function exitFullscreen() {
    if (!_fullscreenEl) return;
    _fullscreenEl.classList.remove('chart-fullscreen');
    _fullscreenEl = null;
    document.getElementById('fullscreen-backdrop')?.remove();
    document.getElementById('fullscreen-close')?.remove();
    document.body.style.overflow = '';
}

// ESC to close fullscreen
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') exitFullscreen();
});

/**
 * Initialize Event Listeners
 */
function init() {
    keywordInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            const val = keywordInput.value.trim();
            if (val) {
                addKeyword(val);
                keywordInput.value = '';
            }
        }
    });

    closeChartBtn.addEventListener('click', resetUI);

    // Initial focus
    keywordInput.focus();

    // Tab switching logic
    const searchPanel = document.getElementById('tab-search');
    const speakersPanel = document.getElementById('tab-speakers');
    const transitionsPanel = document.getElementById('tab-transitions');
    const geoPanel = document.getElementById('tab-geo');
    const coveragePanel = document.getElementById('tab-coverage');
    const searchSection = document.getElementById('search-section');

    const allPanels = [searchPanel, speakersPanel, transitionsPanel, geoPanel, coveragePanel];

    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const tabId = btn.getAttribute('data-tab');

            document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            allPanels.forEach(p => p && p.classList.remove('active'));

            if (tabId === 'search') {
                searchSection.style.display = '';
                searchPanel.classList.add('active');
            } else if (tabId === 'speakers') {
                searchSection.style.display = 'none';
                speakersPanel.classList.add('active');
                if (!state.speakersLoaded) drawSpeakerCalendar();
            } else if (tabId === 'transitions') {
                searchSection.style.display = 'none';
                transitionsPanel.classList.add('active');
                if (!state.transitionsLoaded) drawTransitionGraph();
            } else if (tabId === 'geo') {
                searchSection.style.display = 'none';
                geoPanel.classList.add('active');
                if (!state.geoLoaded) drawGeoMap();
            } else if (tabId === 'coverage') {
                searchSection.style.display = 'none';
                coveragePanel.classList.add('active');
                if (!state.coverageLoaded) drawDataCoverageChart();
            }
        });
    });

    // Make the keyword line chart expandable
    setupExpandable(chartSection);
    setupExpandable(document.getElementById('coverage-chart-wrapper'));

    // Language Switcher Logic
    const langBtns = document.querySelectorAll('.lang-btn');
    langBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const lang = btn.getAttribute('data-lang');
            setLanguage(lang);
        });
    });

    /**
     * Set the application language
     * @param {string} lang 
     */
    function setLanguage(lang) {
        state.language = lang;

        // Update buttons
        document.querySelectorAll('.lang-btn').forEach(b => {
            b.classList.toggle('active', b.getAttribute('data-lang') === lang);
        });

        // Update translations
        const t = TRANSLATIONS[lang];
        document.querySelectorAll('[data-i18n]').forEach(el => {
            const key = el.getAttribute('data-i18n');
            if (t[key]) el.textContent = t[key];
        });

        document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
            const key = el.getAttribute('data-i18n-placeholder');
            if (t[key]) el.setAttribute('placeholder', t[key]);
        });

        // Special case: Subscribe button text (if not in Subscribed state)
        const subBtn = document.getElementById('subscribe-btn');
        if (subBtn && !subBtn.disabled) {
            subBtn.innerText = t['subscribe-btn'];
        }

        // Update tooltips for info buttons
        document.querySelectorAll('.chart-info-btn').forEach(btn => {
            // For now only geo map wrapper has this, but we can generalize if needed
            // If we want it to be fully generic, we could use data-i18n-tooltip
            if (btn.closest('#geo-map-wrapper')) {
                btn.setAttribute('data-tooltip', t['geo-info-text']);
            }
        });
    }

    // Initialize with default language
    setLanguage(state.language);

    // Footer / Newsletter Logic
    const dsgvoTrigger = document.getElementById('dsgvo-trigger');
    const dsgvoModal = document.getElementById('dsgvo-modal');
    const modalClose = document.getElementById('modal-close');
    const newsletterForm = document.getElementById('newsletter-form');

    // Subtitle Modal Elements
    const subtitleModal = document.getElementById('subtitle-modal');
    const subtitleClose = document.getElementById('subtitle-modal-close');

    if (dsgvoTrigger && dsgvoModal && modalClose) {
        dsgvoTrigger.addEventListener('click', () => dsgvoModal.classList.remove('hidden'));
        modalClose.addEventListener('click', () => dsgvoModal.classList.add('hidden'));
        dsgvoModal.addEventListener('click', (e) => {
            if (e.target === dsgvoModal) dsgvoModal.classList.add('hidden');
        });
    }

    if (subtitleClose) {
        subtitleClose.addEventListener('click', () => subtitleModal.classList.add('hidden'));
        subtitleModal.addEventListener('click', (e) => {
            if (e.target === subtitleModal) subtitleModal.classList.add('hidden');
        });
    }

    if (newsletterForm) {
        newsletterForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            const emailInput = document.getElementById('newsletter-email');
            const dsgvoCheckbox = document.getElementById('dsgvo-checkbox');
            const btn = document.getElementById('subscribe-btn');
            const t = TRANSLATIONS[state.language];

            const formData = new FormData();
            formData.append('email', emailInput.value);
            formData.append('dsgvo', dsgvoCheckbox.checked);

            const originalText = t['subscribe-btn'];
            btn.innerText = t['subscribing'];
            btn.disabled = true;

            try {
                const response = await fetch(`${API_BASE_URL}/subscribe`, {
                    method: 'POST',
                    body: formData
                });

                const result = await response.json();

                if (response.ok && !result.message) {
                    btn.innerText = t['subscribed'];
                    const originalBg = btn.style.background;
                    btn.style.background = '#059669'; // Success Green
                    setTimeout(() => {
                        btn.innerText = originalText;
                        btn.style.background = originalBg;
                        btn.disabled = false;
                        newsletterForm.reset();
                    }, 3000);
                } else {
                    alert(result.message || t['sub-failed']);
                    btn.innerText = originalText;
                    btn.disabled = false;
                }
            } catch (err) {
                console.error('Newsletter error:', err);
                alert(t['alert-fail']);
                btn.innerText = originalText;
                btn.disabled = false;
            }
        });
    }
}

/**
 * Show the subtitle for a specific date
 * @param {Date} date 
 */
async function showSubtitle(date) {
    const subtitleModal = document.getElementById('subtitle-modal');
    const title = document.getElementById('subtitle-date-title');
    const textContainer = document.getElementById('subtitle-text-container');
    const dateStr = date.toISOString().split('T')[0];

    const t = TRANSLATIONS[state.language];

    title.innerText = date.toLocaleDateString(state.language === 'de' ? 'de-DE' : 'en-US', {
        day: '2-digit', month: 'long', year: 'numeric'
    });

    textContainer.innerHTML = `<p class="loader-text">${t['loading-subtitle']}</p>`;
    subtitleModal.classList.remove('hidden');

    try {
        const response = await fetch(`${API_BASE_URL}/subtitle/${dateStr}`);
        if (!response.ok) throw new Error('Failed to fetch subtitle');
        const json = await response.json();

        let subtitleText = json.subtitle || t['no-subtitle'];

        // Highlight keywords
        state.keywords.forEach(keyword => {
            const regex = new RegExp(`(${keyword})`, 'gi');
            subtitleText = subtitleText.replace(regex, '<span class="text-marker">$1</span>');
        });

        textContainer.innerHTML = `<p>${subtitleText}</p>`;
    } catch (err) {
        console.error('Subtitle fetch failed:', err);
        textContainer.innerHTML = `<p style="color:var(--accent)">${t['fail-subtitle']}</p>`;
    }
}

/**
 * Add a keyword to the list
 * @param {string} keyword 
 */
function addKeyword(keyword) {
    if (state.keywords.has(keyword)) return;

    state.keywords.add(keyword);
    renderTags();
    performSearch(); // Load data immediately
}

/**
 * Remove a keyword from the list
 * @param {string} keyword 
 */
function removeKeyword(keyword) {
    state.keywords.delete(keyword);
    renderTags();

    if (state.keywords.size === 0) {
        resetUI();
    } else {
        performSearch(); // Refresh chart immediately
    }
}

/**
 * Render tags in the tag container
 */
function renderTags() {
    tagContainer.innerHTML = '';
    const keywordsArray = Array.from(state.keywords);
    keywordsArray.forEach((keyword, index) => {
        const color = COLORS[index % COLORS.length];
        const tag = document.createElement('div');
        tag.className = 'tag';
        tag.style.setProperty('--tag-color', color);

        tag.innerHTML = `
            <span class="tag-color-dot" style="background: ${color}"></span>
            <span class="tag-text">${keyword}</span>
            <span class="tag-remove" data-keyword="${keyword}">&times;</span>
        `;
        tagContainer.appendChild(tag);
    });
}

// Event delegation for tag removal
tagContainer.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('.tag-remove');
    if (removeBtn) {
        const kw = removeBtn.getAttribute('data-keyword');
        removeKeyword(kw);
    }
});

/**
 * Perform search API calls for all keywords
 */
async function performSearch() {
    if (state.keywords.size === 0) return;

    state.isLoading = true;
    updateUIState();

    const keywordsArray = Array.from(state.keywords);
    state.data = {};

    try {
        // Fetch data for all keywords concurrently
        const results = await Promise.all(keywordsArray.map(async (kw) => {
            const response = await fetch(`${API_BASE_URL}/?q=${encodeURIComponent(kw)}&ignorecase=true`);
            if (!response.ok) throw new Error(`API error for ${kw}`);
            const json = await response.json();
            return { keyword: kw, data: json.data };
        }));

        results.forEach(res => {
            state.data[res.keyword] = res.data;
        });

        state.isLoading = false;
        state.chartVisible = true;
        updateUIState();
        renderChart();

    } catch (error) {
        console.error('Search phase failed:', error);
        alert(TRANSLATIONS[state.language]['alert-fail']);
        state.isLoading = false;
        updateUIState();
    }
}

/**
 * Update UI based on loading and chart visibility states
 */
function updateUIState() {
    if (state.isLoading) {
        loader.classList.remove('hidden');
    } else {
        loader.classList.add('hidden');
    }

    if (state.chartVisible) {
        document.body.classList.add('search-top');
        chartSection.classList.add('visible');
        chartSection.classList.remove('hidden');
    } else {
        document.body.classList.remove('search-top');
        chartSection.classList.remove('visible');
        chartSection.classList.add('hidden');
    }
}

/**
 * Reset the UI to initial state
 */
function resetUI() {
    state.chartVisible = false;
    state.data = {};
    state.keywords.clear();
    renderTags();
    updateUIState();
}

/**
 * Render the D3 Chart
 */
function renderChart() {
    const container = document.getElementById('chart-container');
    const legend = document.getElementById('chart-legend');
    container.innerHTML = '';
    legend.innerHTML = '';

    const margin = { top: 20, right: 30, bottom: 80, left: 50 };
    const width = container.clientWidth - margin.left - margin.right;
    const height = container.clientHeight - margin.top - margin.bottom;

    const svg = d3.select('#chart-container')
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

    // Process data: Determine full time range
    const allPoints = [];
    Object.keys(state.data).forEach(kw => {
        state.data[kw].forEach(d => {
            allPoints.push({
                keyword: kw,
                date: new Date(d._id.date),
                count: d.totalOccurences
            });
        });
    });

    if (allPoints.length === 0) {
        container.innerHTML = '<div style="display:flex; justify-content:center; align-items:center; height:100%; color:var(--text-secondary)">No data found for these keywords.</div>';
        return;
    }

    const xExtent = d3.extent(allPoints, d => d.date);

    // Scales
    const x = d3.scaleTime().domain(xExtent).range([0, width]);
    const y = d3.scaleLinear().domain([0, d3.max(allPoints, d => d.count) * 1.1]).range([height, 0]);

    // Line generator with gap handling
    const line = d3.line()
        .defined((d, i, arr) => {
            if (i === 0) return true;
            const prevDate = new Date(arr[i - 1]._id.date);
            const currDate = new Date(d._id.date);
            const diffDays = Math.abs(currDate - prevDate) / (1000 * 60 * 60 * 24);
            return diffDays <= 1.5;
        })
        .x(d => x(new Date(d._id.date)))
        .y(d => y(d.totalOccurences))
        .curve(d3.curveMonotoneX);

    svg.append("defs").append("clipPath")
        .attr("id", "clip")
        .append("rect")
        .attr("width", width)
        .attr("height", height);

    // Brush Tool (Initial definition for closure)
    const brush = d3.brushX()
        .extent([[0, 0], [width, height]])
        .on("end", brushed);

    // Brush Background
    const brushG = svg.append("g")
        .attr("class", "brush")
        .call(brush);

    // Gridlines container
    const gridG = svg.append('g').attr('class', 'grid-container');

    const drawGrid = (xScale) => {
        gridG.selectAll('.grid').remove();
        gridG.append('g')
            .attr('class', 'grid')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(xScale).ticks(8).tickSize(-height).tickFormat(''));
        gridG.append('g')
            .attr('class', 'grid')
            .call(d3.axisLeft(y).ticks(6).tickSize(-width).tickFormat(''));
    };
    drawGrid(x);

    // Axes
    const xAxisG = svg.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(width / 120));

    const yAxisG = svg.append('g')
        .attr('class', 'y-axis')
        .call(d3.axisLeft(y).ticks(6));

    // Chart Area (Lines and dots on top of brush)
    const chartArea = svg.append('g').attr('clip-path', 'url(#clip)');

    const series = Object.keys(state.data).map((kw, i) => ({
        keyword: kw,
        color: COLORS[i % COLORS.length],
        data: state.data[kw].sort((a, b) => new Date(a._id.date) - new Date(b._id.date))
    }));

    // Draw lines and dots
    series.forEach(s => {
        chartArea.append('path')
            .datum(s.data)
            .attr('class', 'line')
            .attr('stroke', s.color)
            .attr('d', line);

        chartArea.selectAll(`.dot-${s.keyword.replace(/\s+/g, '-')}`)
            .data(s.data)
            .enter()
            .append('circle')
            .attr('class', 'dot')
            .attr('data-keyword', s.keyword)
            .attr('fill', s.color)
            .attr('cx', d => x(new Date(d._id.date)))
            .attr('cy', d => y(d.totalOccurences))
            .attr('r', 3)
            .on('click', (e, d) => {
                showSubtitle(new Date(d._id.date));
            });
    });

    function brushed(event) {
        if (!event.sourceEvent) return; // Ignore programmatic moves
        const selection = event.selection;

        if (!selection) {
            x.domain(xExtent);
        } else {
            x.domain([x.invert(selection[0]), x.invert(selection[1])]);
            brushG.call(brush.move, null); // Clear brush selection box
        }

        // Update with transition
        const t = svg.transition().duration(750);

        xAxisG.transition(t).call(d3.axisBottom(x).ticks(width / 100));
        drawGrid(x); // Grid update is static for performance, or we can transition it

        chartArea.selectAll(".line")
            .transition(t)
            .attr("d", line);

        chartArea.selectAll(".dot")
            .transition(t)
            .attr("cx", d => x(new Date(d._id.date)))
            .attr("cy", d => y(d.totalOccurences));
    }
}

// ─── Speaker Calendar ────────────────────────────────────────────────────────

// Colour map — only canonical names get a colour; everything else → grey
const SPEAKER_COLORS = {
    'Susanne Daubner': '#7fc97f',
    'Judith Rakers': '#beaed4',
    'Thorsten Schröder': '#fdc086',
    'Thorsten Schroeder': '#fdc086',
    'Torsten Schröder': '#fdc086',
    'Jan Hofer': '#bf5b17',
    'Linda Zervakis': '#386cb0',
    'Lina Zervakis': '#386cb0',
    'Linka Zervakis': '#386cb0',
    'Jens Riewa': '#f0027f',
    'Jan Riewa': '#f0027f',
    'Jans Riewa': '#f0027f',
    'Constantin Schreiber': '#e7298a',
    'Susanne Holst': '#66c2a5',
    'Pinar Ataly': '#8da0cb',
    'Julia Sen': '#fc8d62',
    'Julia-Niharika Sen': '#fc8d62',
    'Julia Niharika-Sen': '#fc8d62',
    'Kirsten Gerhard': '#a6d854',
    'Ellen Arnhold': '#ffd92f',
    'Claus-Erich Boetzkes': '#e5c494',
    'Susanne Daubners': '#7fc97f',
    'Susanne Dauber': '#7fc97f',
    'Susanne Gaubner': '#7fc97f',
    'Romy Hiller': '#5d7f5dff',
};

// Known canonical names for the legend
const CANONICAL_SPEAKERS = [
    'Susanne Daubner', 'Judith Rakers', 'Thorsten Schröder', 'Jan Hofer',
    'Linda Zervakis', 'Jens Riewa', 'Constantin Schreiber', 'Susanne Holst',
    'Pinar Ataly', 'Julia-Niharika Sen', 'Kirsten Gerhard', 'Ellen Arnhold',
    'Claus-Erich Boetzkes', 'Romy Hiller'
];

async function drawSpeakerCalendar() {
    const calEl = document.getElementById('calendar');
    const legendEl = document.getElementById('speakers-legend');
    const loaderEl = document.getElementById('speakers-loader');

    calEl.innerHTML = '';
    legendEl.innerHTML = '';
    loaderEl.classList.remove('hidden');

    let rawData;
    try {
        const res = await fetch(`${API_BASE_URL}/speakers`);
        if (!res.ok) throw new Error('Failed');
        const json = await res.json();
        rawData = json.data;
    } catch (e) {
        loaderEl.classList.add('hidden');
        calEl.innerHTML = `<p style="color:var(--accent);text-align:center">Failed to load speaker data.</p>`;
        return;
    }

    loaderEl.classList.add('hidden');
    state.speakersLoaded = true;

    // Make calendar expandable (only once)
    const calendarEl = document.getElementById('calendar');
    if (calendarEl && !calendarEl.querySelector('.chart-expand-btn')) {
        setupExpandable(calendarEl);
    }

    // Also draw the weekday heatmap below the calendar
    drawWeekdayHeatmap();

    // Build lookup: day string → speaker
    const lookup = {};
    rawData.forEach(d => { lookup[d.day] = d.speaker; });

    // Resolve colour for a day
    const colorFor = (day) => SPEAKER_COLORS[lookup[day]] || '#e2e8f0';

    // Build orderly legend from canonical list
    CANONICAL_SPEAKERS.forEach(name => {
        const item = document.createElement('div');
        item.className = 'legend-item';
        item.dataset.speaker = name;
        item.innerHTML = `<span class="legend-dot" style="background:${SPEAKER_COLORS[name]}"></span>${name}`;
        item.addEventListener('click', () => toggleSpeakerFilter(name));
        legendEl.appendChild(item);
    });

    // Draw all months in a single continuous flowing row
    const allDates = rawData.map(d => new Date(d.day));
    const minDate = d3.min(allDates);
    const maxDate = d3.max(allDates);

    // All months from first to last data point
    const allMonths = d3.timeMonths(
        d3.timeMonth.floor(minDate),
        d3.timeMonth.ceil(maxDate)
    );

    const cellSize = 10;
    const cellMargin = 2;
    const cellStep = cellSize + cellMargin;

    // Single continuous row — flex-wrap so it wraps to fill available width
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;align-items:flex-start;';
    calEl.appendChild(row);

    const weeksInMonth = (m) => {
        const start = d3.timeWeek.floor(d3.timeMonth.floor(m));
        return d3.timeWeeks(start, d3.timeMonth.offset(m, 1)).length;
    };

    let lastYear = null;

    allMonths.forEach(monthStart => {
        const yr = monthStart.getFullYear();
        const monthDays = d3.timeDays(monthStart, d3.timeMonth.offset(monthStart, 1));
        const cols = weeksInMonth(monthStart);

        // Height: 7 day rows + label space at bottom
        const labelH = 14;
        const width = cols * cellStep + cellMargin;
        const height = 7 * cellStep + cellMargin + labelH;

        // Insert a narrow year-label SVG before January
        if (yr !== lastYear) {
            lastYear = yr;
            const yearSvg = d3.select(row).append('svg')
                .attr('width', 20)
                .attr('height', height);

            yearSvg.append('text')
                .attr('x', 10)
                .attr('y', height / 2)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('transform', `rotate(-90, 10, ${height / 2})`)
                .style('font-family', 'var(--font-sans)')
                .style('font-size', '9px')
                .style('font-weight', '700')
                .style('fill', 'var(--text-muted)')
                .style('letter-spacing', '0.05em')
                .text(yr);
        }

        const svg = d3.select(row).append('svg')
            .attr('class', 'month-svg')
            .attr('width', width)
            .attr('height', height);

        // Month label at bottom
        svg.append('text')
            .attr('class', 'month-name')
            .attr('x', width / 2)
            .attr('y', height - 1)
            .attr('text-anchor', 'middle')
            .style('font-size', '8px')
            .text(d3.timeFormat('%b')(monthStart));

        const weekOffset = +d3.timeFormat('%U')(new Date(yr, monthStart.getMonth(), 1));

        svg.selectAll('rect.day')
            .data(monthDays)
            .enter().append('rect')
            .attr('class', 'day')
            .attr('data-speaker', d => lookup[d3.timeFormat('%Y-%m-%d')(d)] || '')
            .attr('width', cellSize)
            .attr('height', cellSize)
            .attr('rx', 1).attr('ry', 1)
            .attr('fill', d => colorFor(d3.timeFormat('%Y-%m-%d')(d)))
            .attr('x', d => {
                const w = +d3.timeFormat('%U')(d) - weekOffset;
                return w * cellStep + cellMargin;
            })
            .attr('y', d => +d3.timeFormat('%w')(d) * cellStep + cellMargin)
            .on('mouseover', function () { d3.select(this).classed('hover', true); })
            .on('mouseout', function () { d3.select(this).classed('hover', false); })
            .append('title')
            .text(d => {
                const ds = d3.timeFormat('%Y-%m-%d')(d);
                return `${d3.timeFormat('%a, %d %b %Y')(d)}${lookup[ds] ? ' · ' + lookup[ds] : ''}`;
            });
    });
}

/** Toggle filter on a speaker in the legend */
function toggleSpeakerFilter(speakerName) {
    const items = document.querySelectorAll('.legend-item');
    const clickedItem = [...items].find(i => i.dataset.speaker === speakerName);

    const isAlreadyActive = !clickedItem.classList.contains('dimmed') &&
        [...items].some(i => i.classList.contains('dimmed'));

    if (isAlreadyActive) {
        // Reset: show all
        items.forEach(i => i.classList.remove('dimmed'));
        document.querySelectorAll('#calendar rect.day').forEach(r => {
            r.style.opacity = '1';
        });
    } else {
        // Dim all others
        items.forEach(i => {
            i.classList.toggle('dimmed', i.dataset.speaker !== speakerName);
        });
        document.querySelectorAll('#calendar rect.day').forEach(r => {
            const sp = r.getAttribute('data-speaker');
            // Match canonical + known aliases
            const isMatch = SPEAKER_COLORS[sp] === SPEAKER_COLORS[speakerName];
            r.style.opacity = isMatch ? '1' : '0.08';
        });
    }
}

// ─── Speaker × Weekday Heatmap ────────────────────────────────────────────────

async function drawWeekdayHeatmap() {
    const container = document.getElementById('speaker-weekday');
    const loaderEl = document.getElementById('weekday-loader');
    if (!container) return;

    container.innerHTML = '';
    loaderEl.classList.remove('hidden');

    let data, speakers, weekday_totals;
    try {
        const res = await fetch(`${API_BASE_URL}/speaker-weekday`);
        if (!res.ok) throw new Error('failed');
        const json = await res.json();
        data = json.data;
        speakers = json.speakers;
        weekday_totals = json.weekday_totals;
    } catch (e) {
        loaderEl.classList.add('hidden');
        container.innerHTML = `<p style="color:var(--accent);text-align:center">Konnte Wochentagdaten nicht laden.</p>`;
        return;
    }
    loaderEl.classList.add('hidden');

    const DAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

    // Build lookup: speaker → weekday → cell data
    const matrix = {};
    data.forEach(d => {
        if (!matrix[d.speaker]) matrix[d.speaker] = {};
        matrix[d.speaker][d.weekday] = d;
    });

    const speakerTotals = {};
    data.forEach(d => { speakerTotals[d.speaker] = d.total; });

    // Global max ratio_col drives blue scale domain
    const maxColRatio = d3.max(data, d => d.ratio_col) || 1;

    // ── Layout ──────────────────────────────────────────────────────────────────
    const CELL_W = 68;
    const CELL_H = 44;
    const LABEL_W = 150;
    const TOTAL_W = 56;
    const HEADER_H = 32;
    const ROW_GAP = 6;
    const totalW = LABEL_W + 7 * CELL_W + TOTAL_W + 16;
    const totalH = HEADER_H + speakers.length * (CELL_H + ROW_GAP) + 16;

    // ── Toggle pill ─────────────────────────────────────────────────────────────
    const toggleWrapper = document.createElement('div');
    toggleWrapper.className = 'heatmap-toggle';
    toggleWrapper.innerHTML = `
        <button class="heatmap-toggle-btn active" data-mode="row">Pro Sprecher</button>
        <button class="heatmap-toggle-btn"        data-mode="col">Pro Wochentag</button>`;
    container.appendChild(toggleWrapper);

    let currentMode = 'row';

    // ── SVG wrapper ─────────────────────────────────────────────────────────────
    const svgWrap = document.createElement('div');
    svgWrap.style.overflowX = 'auto';
    container.appendChild(svgWrap);

    const svg = d3.select(svgWrap).append('svg')
        .attr('width', totalW)
        .attr('height', totalH)
        .style('font-family', 'var(--font-sans)')
        .style('overflow', 'visible');

    // Column headers
    const headerG = svg.append('g').attr('transform', `translate(${LABEL_W}, 0)`);
    DAYS_DE.forEach((day, i) => {
        headerG.append('text')
            .attr('x', i * CELL_W + CELL_W / 2).attr('y', HEADER_H - 8)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px').style('font-weight', '600')
            .style('fill', 'var(--text-secondary)')
            .text(day);
    });

    svg.append('text')
        .attr('x', LABEL_W + 7 * CELL_W + TOTAL_W / 2).attr('y', HEADER_H - 8)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px').style('font-weight', '600')
        .style('fill', 'var(--text-muted)')
        .text('\u2211');

    // ── Tooltip ─────────────────────────────────────────────────────────────────
    container.style.position = 'relative';
    const tooltip = d3.select(container).append('div')
        .style('position', 'absolute').style('pointer-events', 'none')
        .style('display', 'none').style('background', 'var(--text-primary)')
        .style('color', '#fff').style('padding', '6px 10px')
        .style('border-radius', '8px').style('font-size', '12px')
        .style('line-height', '1.5').style('z-index', '50')
        .style('box-shadow', 'var(--shadow-modal)').style('white-space', 'nowrap');

    // ── Build row scaffold (static elements) ──────────────────────────────────
    const cellRects = {};
    const cellLabels = {};

    speakers.forEach((speaker, rowIdx) => {
        const y = HEADER_H + rowIdx * (CELL_H + ROW_GAP);
        const rowG = svg.append('g').attr('transform', `translate(0, ${y})`);
        const speakerColor = SPEAKER_COLORS[speaker] || '#888';

        rowG.append('text')
            .attr('x', LABEL_W - 12).attr('y', CELL_H / 2).attr('dy', '0.35em')
            .attr('text-anchor', 'end')
            .style('font-size', '12px').style('font-weight', '500')
            .style('fill', 'var(--text-primary)')
            .text(speaker);

        rowG.append('circle')
            .attr('cx', LABEL_W - 5).attr('cy', CELL_H / 2).attr('r', 4)
            .attr('fill', speakerColor);

        DAYS_DE.forEach((_, wdIdx) => {
            const cx = LABEL_W + wdIdx * CELL_W;
            const key = `${speaker}-${wdIdx}`;

            const rect = rowG.append('rect')
                .attr('x', cx).attr('y', 1)
                .attr('width', CELL_W - 4).attr('height', CELL_H - 4).attr('rx', 8)
                .style('cursor', 'default')
                .on('mousemove', (event) => {
                    const cell = matrix[speaker]?.[wdIdx];
                    if (!cell) return;
                    const pctRow = (cell.ratio * 100).toFixed(1);
                    const pctCol = (cell.ratio_col * 100).toFixed(1);
                    const dayTotal = weekday_totals[wdIdx] || 0;
                    const html = currentMode === 'row'
                        ? `<strong>${speaker}</strong><br>${DAYS_DE[wdIdx]}: ${cell.count} Tage &middot; <em>${pctRow}% aller ${speaker}-Sendungen</em>`
                        : `<strong>${speaker}</strong><br>${DAYS_DE[wdIdx]}: ${cell.count} von ${dayTotal} &middot; <em>${pctCol}% aller ${DAYS_DE[wdIdx]}s</em>`;
                    tooltip.style('display', 'block').html(html)
                        .style('left', (event.offsetX + 14) + 'px')
                        .style('top', (event.offsetY - 10) + 'px');
                })
                .on('mouseleave', () => tooltip.style('display', 'none'));

            cellRects[key] = rect;

            const lbl = rowG.append('text')
                .attr('x', cx + (CELL_W - 4) / 2).attr('y', CELL_H / 2).attr('dy', '0.35em')
                .attr('text-anchor', 'middle')
                .style('font-size', '11px').style('font-weight', '600')
                .style('pointer-events', 'none');
            cellLabels[key] = lbl;
        });

        // Sum total badge
        const tx = LABEL_W + 7 * CELL_W + 6;
        rowG.append('rect')
            .attr('x', tx).attr('y', 1)
            .attr('width', TOTAL_W - 8).attr('height', CELL_H - 4).attr('rx', 8)
            .attr('fill', 'var(--glass-border)').attr('fill-opacity', 0.5);
        rowG.append('text')
            .attr('x', tx + (TOTAL_W - 8) / 2).attr('y', CELL_H / 2).attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .style('font-size', '11px').style('font-weight', '600')
            .style('fill', 'var(--text-muted)')
            .text(speakerTotals[speaker] || 0);
    });

    // ── Re-render cells on mode switch ─────────────────────────────────────────
    function renderCells(mode) {
        const blueScale = d3.scaleSequential(d3.interpolate('#dbeafe', '#1e40af'))
            .domain([0, maxColRatio]);

        speakers.forEach(speaker => {
            const speakerColor = SPEAKER_COLORS[speaker] || '#888';
            DAYS_DE.forEach((_, wdIdx) => {
                const key = `${speaker}-${wdIdx}`;
                const rect = cellRects[key];
                const lbl = cellLabels[key];
                const cell = matrix[speaker]?.[wdIdx];

                if (!cell) {
                    rect?.attr('fill', '#f0f0f2');
                    lbl?.text('');
                    return;
                }

                let fill, pct, intensityForText;
                if (mode === 'row') {
                    intensityForText = cell.ratio_norm;
                    pct = (cell.ratio * 100).toFixed(0) + '%';
                    fill = d3.interpolate('#f8f8fa', speakerColor)(0.15 + cell.ratio_norm * 0.75);
                } else {
                    intensityForText = cell.ratio_col / maxColRatio;
                    pct = (cell.ratio_col * 100).toFixed(0) + '%';
                    fill = blueScale(cell.ratio_col);
                }

                rect?.attr('fill', fill);
                lbl?.style('fill', intensityForText > 0.55 ? '#fff' : 'var(--text-secondary)')
                    .text(pct);
            });
        });
    }

    // ── Toggle wiring ──────────────────────────────────────────────────────────
    toggleWrapper.querySelectorAll('.heatmap-toggle-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            currentMode = btn.dataset.mode;
            toggleWrapper.querySelectorAll('.heatmap-toggle-btn')
                .forEach(b => b.classList.toggle('active', b === btn));
            renderCells(currentMode);
        });
    });

    renderCells('row');

    if (!container.querySelector('.chart-expand-btn')) {
        setupExpandable(container);
    }
}


// ─── Speaker Transition Graph ────────────────────────────────────────────────

async function drawTransitionGraph() {
    const container = document.getElementById('transitions-graph');
    const loaderEl = document.getElementById('transitions-loader');

    container.innerHTML = '';
    loaderEl.classList.remove('hidden');

    let nodes, links;
    try {
        const res = await fetch(`${API_BASE_URL}/speaker-transitions`);
        if (!res.ok) throw new Error('failed');
        const json = await res.json();
        nodes = json.nodes;
        links = json.links;
    } catch (e) {
        loaderEl.classList.add('hidden');
        container.innerHTML = `<p style="color:var(--accent);text-align:center">Konnte Übergangsdaten nicht laden.</p>`;
        return;
    }

    loaderEl.classList.add('hidden');
    state.transitionsLoaded = true;

    // Keep only the top N most frequent transitions to avoid clutter
    const TOP_LINKS = 28;
    links = links.slice(0, TOP_LINKS);

    // Attach expand + info buttons (only once)
    if (!container.querySelector('.chart-expand-btn')) {
        setupExpandable(container, {
            infoTooltip: `Zeigt nur die ${TOP_LINKS} häufigsten Übergänge — weniger frequente Verbindungen werden ausgeblendet, um Rauschen zu reduzieren.`
        });
    }

    const W = container.clientWidth || 900;
    const H = Math.max(720, Math.round(W * 0.85));   // tall canvas — plenty of room

    // Scale helpers
    const maxCount = d3.max(links, d => d.count);
    const minCount = d3.min(links, d => d.count);
    const strokeW = d3.scaleSqrt().domain([minCount, maxCount]).range([1.5, 10]);
    const strokeOp = d3.scaleLinear().domain([minCount, maxCount]).range([0.2, 0.75]);

    const maxTotal = d3.max(nodes, d => d.total);
    const radius = d3.scaleSqrt().domain([1, maxTotal]).range([18, 48]);

    // SVG
    const svg = d3.select(container).append('svg')
        .attr('viewBox', `0 0 ${W} ${H}`)
        .attr('width', '100%')
        .style('font-family', 'var(--font-sans)');

    const defs = svg.append('defs');

    // Arrow markers per source colour
    const markerIds = {};
    nodes.forEach(n => {
        const col = SPEAKER_COLORS[n.id] || '#aaa';
        const mid = `arrow-${n.id.replace(/[\s-]+/g, '_')}`;
        markerIds[n.id] = mid;
        defs.append('marker')
            .attr('id', mid)
            .attr('viewBox', '0 -5 10 10')
            .attr('refX', 12).attr('refY', 0)
            .attr('markerWidth', 5).attr('markerHeight', 5)
            .attr('orient', 'auto')
            .append('path').attr('d', 'M0,-5L10,0L0,5').attr('fill', col);
    });

    const g = svg.append('g');
    svg.call(d3.zoom().scaleExtent([0.25, 4]).on('zoom', e => g.attr('transform', e.transform)));

    const simLinks = links.map(d => ({ ...d }));
    const simNodes = nodes.map(d => ({ ...d }));

    // ── Force layout (ForceAtlas2-inspired tuning) ──────────────────────────
    // Strong repulsion + weak link attraction + soft gravity keeps nodes spread
    const sim = d3.forceSimulation(simNodes)
        .force('link', d3.forceLink(simLinks)
            .id(d => d.id)
            .distance(d => {
                // Rare edges (low count) get longer distance — force them to edge of graph
                const ratio = d.count / maxCount;
                return 350 - ratio * 120;   // 230px–350px
            })
            .strength(0.08))               // very loose — nodes don't get sucked together
        .force('charge', d3.forceManyBody()
            .strength(-2800)               // strong repulsion — nodes push far apart
            .distanceMax(600))
        .force('center', d3.forceCenter(W / 2, H / 2).strength(0.04)) // very soft centering
        .force('x', d3.forceX(W / 2).strength(0.03))  // gentle gravity toward center X
        .force('y', d3.forceY(H / 2).strength(0.03))  // gentle gravity toward center Y
        .force('collision', d3.forceCollide(d => radius(d.total) + 20))
        .alphaDecay(0.015)   // slower decay = more time to spread before settling
        .velocityDecay(0.35);

    // Pre-warm: run 200 ticks silently so the sim starts already spread out
    // (avoids the "everything starts in a pile" animation)
    sim.stop();
    for (let i = 0; i < 200; i++) sim.tick();

    // Draw edges (after pre-warm, positions are already set)
    const linkSel = g.append('g').attr('class', 'edges')
        .selectAll('path')
        .data(simLinks)
        .enter().append('path')
        .attr('fill', 'none')
        .attr('stroke', d => SPEAKER_COLORS[d.source.id || d.source] || '#aaa')
        .attr('stroke-width', d => strokeW(d.count))
        .attr('stroke-opacity', d => strokeOp(d.count))
        .attr('marker-end', d => `url(#${markerIds[d.source.id || d.source]})`)
        .attr('class', 'transition-edge');

    linkSel.append('title')
        .text(d => `${d.source.id || d.source} → ${d.target.id || d.target}: ${d.count}×`);

    // Draw node groups
    const nodeSel = g.append('g').attr('class', 'nodes')
        .selectAll('g')
        .data(simNodes)
        .enter().append('g')
        .attr('class', 'transition-node')
        .call(d3.drag()
            .on('start', (e, d) => { if (!e.active) sim.alphaTarget(0.15).restart(); d.fx = d.x; d.fy = d.y; })
            .on('drag', (e, d) => { d.fx = e.x; d.fy = e.y; })
            .on('end', (e, d) => { if (!e.active) sim.alphaTarget(0); d.fx = null; d.fy = null; }));

    nodeSel.append('circle')
        .attr('r', d => radius(d.total))
        .attr('fill', d => SPEAKER_COLORS[d.id] || '#ccc')
        .attr('fill-opacity', 0.92)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2.5);

    // Initials (inside circle)
    nodeSel.append('text')
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .style('font-size', d => `${Math.max(9, radius(d.total) * 0.4)}px`)
        .style('font-weight', '700')
        .style('fill', '#fff')
        .style('pointer-events', 'none')
        .text(d => d.id.split(' ').map(w => w[0]).join(''));

    // Full name (below circle)
    nodeSel.append('text')
        .attr('dy', d => radius(d.total) + 15)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', '500')
        .style('fill', 'var(--text-secondary)')
        .style('pointer-events', 'none')
        .text(d => d.id);

    // Hover: highlight edges
    nodeSel.on('mouseover', (_, hovered) => {
        const name = hovered.id;
        linkSel.each(function (d) {
            const src = d.source.id || d.source;
            const tgt = d.target.id || d.target;
            d3.select(this)
                .attr('stroke-opacity', src === name ? 0.9 : tgt === name ? 0.45 : 0.03)
                .attr('stroke-width', (src === name || tgt === name) ? strokeW(d.count) + 1.5 : strokeW(d.count));
        });
        nodeSel.attr('opacity', d => d.id === name ? 1 : 0.25);
    }).on('mouseout', () => {
        linkSel.attr('stroke-opacity', d => strokeOp(d.count))
            .attr('stroke-width', d => strokeW(d.count));
        nodeSel.attr('opacity', 1);
    });

    // Curved arc path — endpoint shortened to avoid overlapping node circle
    function arcPath(d) {
        const sx = d.source.x, sy = d.source.y;
        const tx0 = d.target.x, ty0 = d.target.y;
        const dx = tx0 - sx, dy = ty0 - sy;
        const len = Math.max(1, Math.sqrt(dx * dx + dy * dy));
        const tR = radius(d.target.total) + 10;
        const tx = tx0 - (dx / len) * tR;
        const ty = ty0 - (dy / len) * tR;
        // Quadratic bezier with perpendicular control point offset
        const cx = (sx + tx) / 2 + dy * 0.18;
        const cy = (sy + ty) / 2 - dx * 0.18;
        return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
    }

    // Tick function — resume gentle animation after pre-warm
    sim.on('tick', () => {
        g.selectAll('.transition-edge').attr('d', arcPath);
        nodeSel.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Render pre-warmed positions immediately, then resume gentle settling
    g.selectAll('.transition-edge').attr('d', arcPath);
    nodeSel.attr('transform', d => `translate(${d.x},${d.y})`);
    sim.alpha(0.1).restart();  // gentle final settle from pre-warmed positions
}

async function drawDataCoverageChart() {
    const container = document.getElementById('coverage-chart');
    const loaderEl = document.getElementById('coverage-loader');
    if (!container || !loaderEl) return;

    container.innerHTML = '';
    loaderEl.classList.remove('hidden');

    try {
        const res = await fetch(`${API_BASE_URL}/data-coverage`);
        const json = await res.json();
        state.coverageData = json.data;
    } catch (e) {
        console.error("Coverage Error:", e);
        loaderEl.classList.add('hidden');
        container.innerHTML = `<p style="color:var(--accent);text-align:center">Konnte Datenabdeckung nicht laden.</p>`;
        return;
    }

    loaderEl.classList.add('hidden');
    state.coverageLoaded = true;

    const width = container.offsetWidth || 1000;
    const height = 400;
    const margin = { top: 20, right: 30, bottom: 50, left: 60 };

    const svg = d3.select(container).append('svg')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('width', '100%')
        .attr('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Helper: get days in month
    const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();

    // Prepare data
    const data = state.coverageData.map(d => ({
        date: new Date(d.year, d.month - 1, 1),
        found: d.count,
        total: getDaysInMonth(d.year, d.month),
        missing: Math.max(0, getDaysInMonth(d.year, d.month) - d.count)
    }));

    const x = d3.scaleTime()
        .domain(d3.extent(data, d => d.date))
        .range([0, width - margin.left - margin.right]);

    const y = d3.scaleLinear()
        .domain([0, 31])
        .range([height - margin.top - margin.bottom, 0]);

    // Stack
    const stack = d3.stack().keys(['found', 'missing']);
    const series = stack(data);

    // Color: Tagesschau Blue (found) vs Light Grey (missing)
    const color = d3.scaleOrdinal()
        .domain(['found', 'missing'])
        .range(['var(--accent-blue)', '#e2e8f0']);

    g.append('g')
        .selectAll('g')
        .data(series)
        .join('g')
        .attr('fill', d => color(d.key))
        .selectAll('rect')
        .data(d => d)
        .join('rect')
        .attr('x', d => x(d.data.date))
        .attr('y', d => y(d[1]))
        .attr('height', d => y(d[0]) - y(d[1]))
        .attr('width', (width - margin.left - margin.right) / data.length * 0.8)
        .attr('rx', 2);

    // Axes
    const xAxis = d3.axisBottom(x).ticks(10).tickFormat(d3.timeFormat("%Y"));
    const yAxis = d3.axisLeft(y).ticks(5);

    g.append('g')
        .attr('transform', `translate(0,${height - margin.top - margin.bottom})`)
        .attr('class', 'x-axis')
        .call(xAxis);

    g.append('g')
        .call(yAxis)
        .attr('class', 'y-axis');

    // Tooltip
    const tooltip = d3.select(container).append('div')
        .attr('class', 'map-tooltip')
        .style('position', 'absolute')
        .style('display', 'none')
        .style('background', 'rgba(15, 23, 42, 0.95)')
        .style('color', '#fff')
        .style('padding', '8px 12px')
        .style('border-radius', '8px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '100');

    g.selectAll('rect')
        .on('mouseover', function (event, d) {
            const data = d.data;
            tooltip.style('display', 'block')
                .html(`<strong>${d3.timeFormat("%B %Y")(data.date)}</strong><br>
                       Vorhanden: ${data.found} Tage<br>
                       Gesamt: ${data.total} Tage<br>
                       Abdeckung: ${((data.found / data.total) * 100).toFixed(1)}%`);
        })
        .on('mousemove', (event) => {
            tooltip.style('left', (event.offsetX + 15) + 'px')
                .style('top', (event.offsetY - 25) + 'px');
        })
        .on('mouseleave', () => tooltip.style('display', 'none'));
}

async function drawGeoMap() {
    const container = document.getElementById('geo-map');
    const loaderEl = document.getElementById('geo-loader');
    const sliderContainer = document.getElementById('geo-slider-container');
    if (!container || !sliderContainer) return;

    // 1. Get available data range and density if not already in state
    if (!state.geoDateRange.start) {
        try {
            const [rangeRes, densityRes] = await Promise.all([
                fetch(`${API_BASE_URL}/data-range`),
                fetch(`${API_BASE_URL}/geo-density`)
            ]);
            const range = await rangeRes.json();
            const density = await densityRes.json();

            state.geoDensity = density.data.map(d => ({
                date: new Date(d.year, d.month - 1, 1),
                count: d.count
            }));

            if (range.min_date && range.max_date) {
                const maxDate = new Date(range.max_date);
                const minDate = new Date(range.min_date);

                // Default: last 20 years
                const initialStart = new Date(maxDate);
                initialStart.setFullYear(initialStart.getFullYear() - 20);

                state.geoDateRange = {
                    min: minDate,
                    max: maxDate,
                    start: initialStart < minDate ? minDate : initialStart,
                    end: maxDate
                };
            }
        } catch (e) {
            console.error("Failed to fetch geo meta data", e);
        }
    }

    const { start, end, min, max } = state.geoDateRange;
    if (!start || !end) {
        const failText = TRANSLATIONS[state.language]['geo-range-fail'];
        container.innerHTML = `<p style="color:var(--accent);text-align:center">${failText}</p>`;
        return;
    }

    // 2. Clear and Show Loader
    container.innerHTML = '';
    sliderContainer.innerHTML = '';
    loaderEl.classList.remove('hidden');

    let countryMentions, geoData;
    try {
        const startStr = start.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];

        const [resMentions, resGeo] = await Promise.all([
            fetch(`${API_BASE_URL}/mentioned-locations?start_date=${startStr}&end_date=${endStr}`),
            fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson')
        ]);
        if (!resMentions.ok || !resGeo.ok) throw new Error('API or GeoJSON failed');
        countryMentions = (await resMentions.json()).data;
        geoData = await resGeo.json();
    } catch (e) {
        console.error("GeoMap Error:", e);
        loaderEl.classList.add('hidden');
        container.innerHTML = `<p style="color:var(--accent);text-align:center">Konnte Geo-Daten nicht laden.</p>`;
        return;
    }
    loaderEl.classList.add('hidden');
    state.geoLoaded = true;

    // Check if any mentions found
    const hasData = countryMentions.some(d => d.count > 0);

    // 3. Setup Slider
    const margin = { top: 0, right: 20, bottom: 0, left: 20 };
    const containerWidth = sliderContainer.offsetWidth || 800;
    const sWidth = containerWidth - margin.left - margin.right;
    const sHeight = 60;

    const x = d3.scaleTime()
        .domain([min, max])
        .range([0, sWidth]);

    const sSvg = d3.select(sliderContainer).append("svg")
        .attr("width", sWidth + margin.left + margin.right)
        .attr("height", sHeight)
        .append("g")
        .attr("transform", `translate(${margin.left}, 15)`);

    // Add Density Area Chart as Background
    if (state.geoDensity.length > 0) {
        const yDens = d3.scaleLinear()
            .domain([0, d3.max(state.geoDensity, d => d.count)])
            .range([20, 0]);

        const area = d3.area()
            .x(d => x(d.date))
            .y0(20)
            .y1(d => yDens(d.count))
            .curve(d3.curveBasis);

        sSvg.append("path")
            .datum(state.geoDensity)
            .attr("fill", "var(--accent-blue)")
            .attr("opacity", 0.08)
            .attr("d", area);
    }

    sSvg.append("g")
        .attr("class", "slider-axis")
        .attr("transform", `translate(0, 20)`)
        .call(d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat("%Y")));

    const brush = d3.brushX()
        .extent([[0, 0], [sWidth, 20]])
        .on("end", (event) => {
            if (!event.selection || !event.sourceEvent) return;
            const [x0, x1] = event.selection.map(x.invert);
            // Snap to days
            x0.setHours(0, 0, 0, 0);
            x1.setHours(0, 0, 0, 0);

            if (x0.getTime() !== state.geoDateRange.start.getTime() || x1.getTime() !== state.geoDateRange.end.getTime()) {
                state.geoDateRange.start = x0;
                state.geoDateRange.end = x1;
                drawGeoMap(); // Re-fetch and re-draw
            }
        });

    const brushG = sSvg.append("g")
        .attr("class", "slider-brush")
        .call(brush)
        .call(brush.move, [x(start), x(end)]);

    // Label for current range
    const label = sSvg.append("text")
        .attr("class", "slider-label")
        .attr("x", sWidth / 2)
        .attr("y", -5)
        .text(`${start.toLocaleDateString()} - ${end.toLocaleDateString()}`);

    // Update labels during brushing
    brush.on("brush", (event) => {
        if (!event.selection) return;
        const [x0, x1] = event.selection.map(x.invert);
        label.text(`${x0.toLocaleDateString()} - ${x1.toLocaleDateString()}`);
    });

    // 4. Draw Map
    if (!hasData) {
        const noDataText = TRANSLATIONS[state.language]['geo-no-data'];
        container.innerHTML = `<p style="padding: 2rem; color: var(--text-muted); text-align: center;">${noDataText}</p>`;
        return;
    }

    const mentionMap = new Map();
    countryMentions.forEach(d => {
        if (d.iso !== "UNK") mentionMap.set(d.iso, d);
    });

    const width = container.offsetWidth || 900;
    const height = 500;

    const svg = d3.select(container).append('svg')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('width', '100%')
        .attr('height', 'auto')
        .style('cursor', 'grab');

    const g = svg.append('g');

    // Zoom behavior
    const zoom = d3.zoom()
        .scaleExtent([1, 8])
        .on('start', () => svg.style('cursor', 'grabbing'))
        .on('zoom', (event) => {
            g.attr('transform', event.transform);
        })
        .on('end', () => svg.style('cursor', 'grab'));

    svg.call(zoom);

    const projection = d3.geoMercator()
        .scale(width / 6.5)
        .translate([width / 2, height / 1.5]);

    const path = d3.geoPath().projection(projection);

    const tooltip = d3.select(container).append('div')
        .attr('class', 'map-tooltip')
        .style('position', 'absolute')
        .style('display', 'none')
        .style('background', 'rgba(15, 23, 42, 0.95)')
        .style('color', '#fff')
        .style('padding', '8px 12px')
        .style('border-radius', '8px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '100')
        .style('box-shadow', 'var(--shadow-modal)');

    let currentMode = 'abs';

    function updateMap(mode) {
        const maxAbs = d3.max(countryMentions, d => d.count) || 1;
        const perCapValues = countryMentions.map(d => d.pop > 0 ? (d.count / d.pop) * 1000000 : 0);
        const maxPer = d3.max(perCapValues) || 1;

        // Use logarithmic scale for better balance of extremes
        const colorScaleAbs = d3.scaleSequential(d3.interpolateBlues).domain([0, Math.log1p(maxAbs)]);
        const colorScalePer = d3.scaleSequential(d3.interpolateOranges).domain([0, Math.log1p(maxPer)]);

        g.selectAll("path")
            .data(geoData.features)
            .join("path")
            .attr("d", path)
            .attr("stroke", "var(--glass-border)")
            .attr("stroke-width", 0.5)
            .transition().duration(750)
            .attr("fill", d => {
                const data = mentionMap.get(d.id);
                if (!data) return "#f0f0f2";
                if (mode === 'abs') {
                    return colorScaleAbs(Math.log1p(data.count));
                } else {
                    const perM = data.pop > 0 ? (data.count / data.pop) * 1000000 : 0;
                    return colorScalePer(Math.log1p(perM));
                }
            });

        g.selectAll("path")
            .on("mouseover", function (event, d) {
                const data = mentionMap.get(d.id);
                const name = d.properties.name || d.id;
                d3.select(this).attr("stroke", "var(--accent-blue)").attr("stroke-width", 1.5);

                let valText = "Keine Daten";
                if (data) {
                    if (mode === 'abs') {
                        valText = `${data.count} Erwähnungen`;
                    } else {
                        const perM = data.pop > 0 ? ((data.count / data.pop) * 1000000).toFixed(2) : 0;
                        valText = `${perM} Erwähnungen pro 1 Mio. EW`;
                    }
                }

                tooltip.style('display', 'block')
                    .html(`<strong>${name}</strong><br>${valText}`);
            })
            .on("mousemove", (event) => {
                tooltip.style('left', (event.offsetX + 15) + 'px')
                    .style('top', (event.offsetY - 25) + 'px');
            })
            .on("mouseleave", function () {
                d3.select(this).attr("stroke", "var(--glass-border)").attr("stroke-width", 0.5);
                tooltip.style('display', 'none');
            });
    }

    // Toggle logic
    document.querySelectorAll('#tab-geo .heatmap-toggle-btn').forEach(btn => {
        btn.onclick = () => {
            currentMode = btn.dataset.mode;
            document.querySelectorAll('#tab-geo .heatmap-toggle-btn').forEach(b => b.classList.toggle('active', b === btn));
            updateMap(currentMode);
        };
    });

    updateMap('abs');

    const infoText = TRANSLATIONS[state.language]['geo-info-text'];
    setupExpandable(document.getElementById('geo-map-wrapper'), { infoTooltip: infoText });
}

// Start the app
init();
